import { HowToAnswer } from "./HowToAnswer";
import { useState } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";

/**
 * SwipeBlock — a stack of short prompts with three answers: yes, no, or
 * "depends". "Depends" is the ONE place a midpoint is legal — it means
 * "I need context to answer" and scores as 50; a screen that uses swipe
 * for a virtue statement is a bug.
 *
 * Interactions:
 *   - Swipe right = yes, left = no.
 *   - Tap the "Depends" pill (below) or press ↓ to mark depends.
 *   - Keyboard: → yes · ← no · ↓ depends.
 */

export type SwipeAnswer = "yes" | "no" | "depends";

export interface SwipePrompt {
  id: string;
  text: string;
  /**
   * Short, deliberately ordinary illustration so an abstract item can be
   * pictured — "like a dentist or a physiotherapist". Examples must be
   * neutral: never aspirational, never off-putting, and never nudging the
   * learner towards yes or no. If no neutral example exists for an item,
   * it gets none.
   */
  example?: string;
}


export interface SwipeBlockProps {
  prompts: SwipePrompt[];
  yesLabel?: string;
  noLabel?: string;
  dependsLabel?: string;
  onComplete: (answers: Record<string, SwipeAnswer>) => void;
}

const VALUE_MAP: Record<SwipeAnswer, number> = { yes: 100, no: 0, depends: 50 };

/** Helper for wiring code — convert a SwipeAnswer to a 0..100 slot value. */
export function swipeToValue(a: SwipeAnswer): number { return VALUE_MAP[a]; }

export function SwipeBlock({
  prompts, yesLabel = "Yes", noLabel = "Not really", dependsLabel = "Depends",
  onComplete,
}: SwipeBlockProps) {
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState<Record<string, SwipeAnswer>>({});
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-8, 8]);
  const yesGlow = useTransform(x, [0, 120], [0, 1]);
  const noGlow = useTransform(x, [-120, 0], [1, 0]);

  const current = prompts[i];

  function answer(v: SwipeAnswer) {
    if (!current) return;
    const next = { ...answers, [current.id]: v };
    setAnswers(next);
    if (i + 1 >= prompts.length) {
      onComplete(next);
    } else {
      setI(i + 1);
      x.set(0);
    }
  }

  // Keyboard.
  function onKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowRight") answer("yes");
    else if (e.key === "ArrowLeft") answer("no");
    else if (e.key === "ArrowDown") answer("depends");
  }

  return (
    <div onKeyDown={onKey} tabIndex={0} className="focus:outline-none">
      <HowToAnswer>Answer each card with one of the buttons below — or swipe right for yes, left for no.</HowToAnswer>
      <div className="relative h-72 md:h-80">
        <AnimatePresence>
          {current && (
            <motion.div
              key={current.id}
              style={{ x, rotate }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              onDragEnd={(_, info) => {
                if (info.offset.x > 100) answer("yes");
                else if (info.offset.x < -100) answer("no");
              }}
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 rounded-3xl border border-border bg-card p-8
                flex items-center justify-center text-center cursor-grab active:cursor-grabbing"
            >
              <motion.div
                style={{ opacity: yesGlow }}
                className="absolute inset-0 rounded-3xl bg-primary/10 pointer-events-none"
              />
              <motion.div
                style={{ opacity: noGlow }}
                className="absolute inset-0 rounded-3xl bg-destructive/10 pointer-events-none"
              />
              <div className="relative">
                <p className="font-display text-2xl md:text-3xl leading-snug">
                  {current.text}
                </p>
                {current.example && (
                  <p className="mt-3 text-sm md:text-base text-muted-foreground">
                    {current.example}
                  </p>
                )}
              </div>

            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="flex items-center justify-between gap-3 mt-6">
        <button
          onClick={() => answer("no")}
          className="flex-1 min-h-[48px] rounded-2xl border border-border bg-card hover:bg-secondary
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {noLabel}
        </button>
        <button
          onClick={() => answer("depends")}
          className="min-h-[48px] px-4 rounded-2xl border border-dashed border-border text-muted-foreground
            hover:text-foreground hover:border-foreground/40
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {dependsLabel}
        </button>
        <button
          onClick={() => answer("yes")}
          className="flex-1 min-h-[48px] rounded-2xl bg-primary text-primary-foreground
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {yesLabel}
        </button>
      </div>
      <div className="mt-3 text-xs text-muted-foreground text-center tabular-nums">
        {i + 1} / {prompts.length}
      </div>
    </div>
  );
}
